// Invite a friend (FR-72): one SMS or email with a sign-up link. The server
// answers 202 even when the friend was invited before, and does not keep the
// number or address.
import { Button, Paper, SegmentedControl, Stack, Text, TextInput, Title } from "@mantine/core";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client";
import { errorMessage } from "../../shared/session/errors";
import { FormError, Notice } from "../../shared/ui/Page";

type Channel = "sms" | "email";

export function InviteSection() {
  const { t, i18n } = useTranslation();
  const [channel, setChannel] = useState<Channel>("sms");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setNotice(null);
    setError(null);
    setBusy(true);
    const { error, response } = await api.POST("/me/invite", {
      body: { channel, to: to.trim(), locale: i18n.language === "sw" ? "sw" : "en" },
    });
    setBusy(false);
    if (response.status === 429) return setError(t("invite.too_many"));
    if (!response.ok) return setError(errorMessage(error, t("error.generic")));
    setTo("");
    setNotice(t("invite.sent"));
  };

  return (
    <Paper withBorder p="md" radius="md" component="section" aria-labelledby="invite-title">
      <form onSubmit={submit} data-testid="invite-form">
        <Stack gap="sm">
          <Title order={2} size="h4" id="invite-title">
            {t("invite.title")}
          </Title>
          <Text size="sm">{t("invite.intro")}</Text>
          <Stack gap={4}>
            <Text size="sm" fw={500} id="invite-channel">
              {t("invite.channel")}
            </Text>
            <SegmentedControl
              aria-labelledby="invite-channel"
              w="fit-content"
              value={channel}
              onChange={(v) => {
                setChannel(v as Channel);
                setTo("");
              }}
              data={[
                { value: "sms", label: t("invite.sms") },
                { value: "email", label: t("invite.email") },
              ]}
            />
          </Stack>
          <TextInput
            type={channel === "sms" ? "tel" : "email"}
            inputMode={channel === "sms" ? "tel" : "email"}
            label={channel === "sms" ? t("invite.to_sms") : t("invite.to_email")}
            description={t("invite.privacy")}
            value={to}
            onChange={(e) => setTo(e.currentTarget.value)}
            maxLength={254}
            autoComplete="off"
            required
          />
          {notice && <Notice>{notice}</Notice>}
          <FormError message={error} />
          <Button type="submit" variant="light" w="fit-content" loading={busy}>
            {t("invite.submit")}
          </Button>
        </Stack>
      </form>
    </Paper>
  );
}
